/**
 * StaffInvoices — IT-SCE Intern: Rechnungen aller Kunden.
 *
 * TQ #9695 (Staff-Migration Teil B), portiert von
 * customer-portal/src/pages/staff/StaffInvoices.tsx. Backend admin-api
 * GET /admin/invoices, /admin/invoices/stats, /admin/invoices/overdue
 * (services/api.ts staffListInvoices/staffGetInvoiceStats/staffListOverdueInvoices).
 */

import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { StaffInvoice, StaffInvoiceStats } from '../services/api'
import { useToast } from '../components/ui'

const STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
  Paid: { bg: '#d1fae5', fg: '#065f46' },
  Unpaid: { bg: '#fef3c7', fg: '#92400e' },
  Overdue: { bg: '#fee2e2', fg: '#991b1b' },
  Cancelled: { bg: '#f3f4f6', fg: '#374151' },
}

function fmtEUR(n: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)
}
function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('de-DE')
}

export default function StaffInvoices() {
  const [invoices, setInvoices] = useState<StaffInvoice[]>([])
  const [stats, setStats] = useState<StaffInvoiceStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'overdue' | 'unpaid' | 'paid'>('all')
  const { showToast } = useToast()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const [statsResp, listResp] = await Promise.all([
          api.staffGetInvoiceStats(),
          filter === 'overdue' ? api.staffListOverdueInvoices() : api.staffListInvoices(),
        ])
        if (cancelled) return
        setStats(statsResp)
        let list = listResp.invoices ?? []
        if (filter === 'unpaid') list = list.filter(i => i.status === 'Unpaid')
        if (filter === 'paid') list = list.filter(i => i.status === 'Paid')
        setInvoices(list)
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
        showToast('error', `Fehler beim Laden: ${msg}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>📑 Alle Rechnungen</h1>
        <p className="page-subtitle">Sales Invoices aller Kunden</p>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <StatCard label="Gesamt" value={String(stats.total_count)} />
          <StatCard label="Offen" value={fmtEUR(stats.total_outstanding)} highlight={stats.total_outstanding > 0 ? '#92400e' : undefined} />
          <StatCard label="Überfällig" value={fmtEUR(stats.total_overdue)} highlight={stats.total_overdue > 0 ? '#991b1b' : undefined} />
          <StatCard label="Bezahlt" value={fmtEUR(stats.total_paid)} highlight="#065f46" />
          <StatCard label="Diesen Monat" value={`${stats.this_month_count} · ${fmtEUR(stats.this_month_total)}`} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['all', 'overdue', 'unpaid', 'paid'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 14px',
              background: filter === f ? '#2563eb' : '#fff',
              color: filter === f ? '#fff' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            {f === 'all' ? 'Alle' : f === 'overdue' ? 'Überfällig' : f === 'unpaid' ? 'Offen' : 'Bezahlt'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Lade Rechnungen…</div>
      ) : invoices.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine Rechnungen.</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <Th>Rechnung</Th>
                <Th>Kunde</Th>
                <Th>Datum</Th>
                <Th>Fällig</Th>
                <Th align="right">Betrag</Th>
                <Th align="right">Offen</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const badge = STATUS_BADGE[inv.status] ?? { bg: '#f3f4f6', fg: '#374151' }
                return (
                  <tr key={inv.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <Td><code>{inv.id}</code></Td>
                    <Td>{inv.customer_name || inv.customer}</Td>
                    <Td>{fmtDate(inv.posting_date)}</Td>
                    <Td>{fmtDate(inv.due_date)}</Td>
                    <Td align="right">{fmtEUR(inv.grand_total)}</Td>
                    <Td align="right">
                      <strong style={{ color: inv.outstanding_amount > 0 ? '#991b1b' : '#065f46' }}>
                        {fmtEUR(inv.outstanding_amount)}
                      </strong>
                    </Td>
                    <Td>
                      <span style={{ padding: '2px 8px', background: badge.bg, color: badge.fg, borderRadius: 12, fontSize: 12 }}>
                        {inv.status}
                      </span>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: string }) {
  return (
    <div style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ color: highlight ?? '#111827', fontSize: 20, fontWeight: 600 }}>{value}</div>
    </div>
  )
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th style={{ padding: '10px 12px', textAlign: align ?? 'left', fontWeight: 600, color: '#374151' }}>{children}</th>
}
function Td({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td style={{ padding: '10px 12px', textAlign: align ?? 'left' }}>{children}</td>
}
